import { Injectable, inject, signal } from '@angular/core';
import { Observable, Subject, throwError } from 'rxjs';

import { MfaService } from './mfa.service';
import {
  ChallengeResult,
  MfaAction,
  MfaCancelledError,
  MFA_ACTION_LABELS,
  VerifyResult
} from './mfa.types';

/** Modal step. `sending` covers both the initial challenge and a resend. */
export type MfaStep = 'sending' | 'otp' | 'verifying' | 'phone-missing';

const CHALLENGE_TTL_SECONDS = 10 * 60;   // server expires the challenge after 10m
const RESEND_AFTER_SECS     = 60;        // allow "Resend" 60s after a code is sent

/**
 * Singleton coordinator that bridges the MFA interceptor and the global modal.
 * The interceptor calls `requestToken(action)` and subscribes; the modal binds
 * to the signals and calls `submitOtp` / `resend` / `cancel`. The returned
 * observable emits the single-use mfaToken (then completes) or errors with
 * `MfaCancelledError` if the user dismisses the modal.
 */
@Injectable({ providedIn: 'root' })
export class MfaChallengeService {
  private readonly mfa = inject(MfaService);

  readonly visible     = signal(false);
  readonly actionLabel = signal('');
  readonly step        = signal<MfaStep>('sending');
  readonly inlineError = signal<string | null>(null);
  readonly secondsLeft = signal(CHALLENGE_TTL_SECONDS);
  readonly secondsSent = signal(0);

  private action: MfaAction | null = null;
  private challengeId: string | null = null;
  private result$: Subject<string> | null = null;
  private countdownTimer: ReturnType<typeof setInterval> | null = null;

  /** Entry point for the interceptor: open the modal, request a code, and
   *  resolve with the mfaToken (or error with MfaCancelledError). */
  requestToken(action: MfaAction): Observable<string> {
    // Single-flight: a challenge is already on screen. Reject the new request
    // rather than clobbering the in-progress flow.
    if (this.result$) {
      return throwError(() => new MfaCancelledError('Another verification is already in progress.'));
    }
    this.action      = action;
    this.challengeId = null;
    this.result$     = new Subject<string>();
    this.actionLabel.set(MFA_ACTION_LABELS[action]);
    this.inlineError.set(null);
    this.step.set('sending');
    this.visible.set(true);
    this.sendChallenge();
    return this.result$.asObservable();
  }

  submitOtp(otp: string): void {
    if (!this.challengeId || this.step() === 'verifying') { return; }
    this.inlineError.set(null);
    this.step.set('verifying');
    this.mfa.verify(this.challengeId, otp.trim()).subscribe(r => this.applyVerify(r));
  }

  /** Request a fresh code — used by the Resend link and the expired path. */
  resend(): void {
    if (this.step() === 'sending' || !this.action) { return; }
    this.inlineError.set(null);
    this.step.set('sending');
    this.sendChallenge();
  }

  /** User dismissed the modal — abort the original protected request. */
  cancel(): void {
    const pending = this.result$;
    if (!pending) { return; }
    this.teardown();
    pending.error(new MfaCancelledError());
  }

  readonly canResend = (): boolean =>
    (this.step() === 'otp') &&
    (this.secondsSent() >= RESEND_AFTER_SECS || this.secondsLeft() === 0);

  formatCountdown(): string {
    const s = Math.max(0, this.secondsLeft());
    const m = Math.floor(s / 60);
    const r = s % 60;
    return `${m}:${r.toString().padStart(2, '0')}`;
  }

  // ---- internals -----------------------------------------------------------

  private sendChallenge(): void {
    const action = this.action;
    if (!action) { return; }
    this.mfa.challenge(action).subscribe(r => this.applyChallenge(r));
  }

  private applyChallenge(r: ChallengeResult): void {
    switch (r.kind) {
      case 'sent':
        this.challengeId = r.challengeId;
        this.step.set('otp');
        this.startCountdown();
        return;
      case 'phone-missing':
        this.stopCountdown();
        this.inlineError.set(r.message);
        this.step.set('phone-missing');
        return;
      case 'unavailable':
        // Leave the user on the OTP step so the Resend link can retry.
        this.inlineError.set(r.message);
        this.step.set('otp');
        return;
    }
  }

  private applyVerify(r: VerifyResult): void {
    switch (r.kind) {
      case 'verified': {
        const pending = this.result$;
        const token   = r.mfaToken;
        this.teardown();
        pending?.next(token);
        pending?.complete();
        return;
      }
      case 'incorrect':
        this.inlineError.set(r.message);
        this.step.set('otp');
        return;
      case 'expired':
      case 'locked':
        // Both require a fresh code — stop the clock and surface Resend.
        this.stopCountdown();
        this.secondsLeft.set(0);
        this.inlineError.set(r.message);
        this.step.set('otp');
        return;
      case 'unavailable':
        this.inlineError.set(r.message);
        this.step.set('otp');
        return;
    }
  }

  private teardown(): void {
    this.stopCountdown();
    this.visible.set(false);
    this.result$     = null;
    this.action      = null;
    this.challengeId = null;
    this.step.set('sending');
    this.inlineError.set(null);
  }

  private startCountdown(): void {
    this.stopCountdown();
    this.secondsLeft.set(CHALLENGE_TTL_SECONDS);
    this.secondsSent.set(0);
    this.countdownTimer = setInterval(() => {
      this.secondsLeft.update(v => Math.max(0, v - 1));
      this.secondsSent.update(v => v + 1);
      if (this.secondsLeft() === 0) { this.stopCountdown(); }
    }, 1000);
  }

  private stopCountdown(): void {
    if (this.countdownTimer) {
      clearInterval(this.countdownTimer);
      this.countdownTimer = null;
    }
  }
}
